import { Canvas, useThree } from '@react-three/fiber'
import { Perf } from 'r3f-perf'
import TripleSceneContents from './components/TripleSceneContents.jsx'
import { MoveButton, Hotspots, HotspotsOverlay, Title, TextBox, StandaloneBackButton, Scene2BackButtonTablet, Scene2BackButtonDesktop, LogoGroup, WoomeraBadge, StageSpecs } from './components/UI.jsx'
import { AnimationProvider, useAnimation } from './components/animations.jsx'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { EffectComposer, Bloom, Vignette, HueSaturation, Noise, Sepia, ToneMapping } from '@react-three/postprocessing'
import { BlendFunction, ToneMappingMode } from 'postprocessing'
import { Leva } from 'leva'
import { DEFAULT_CAMERA_POSITIONS } from './components/camera/CameraConfig.js'

// Detect if device is mobile or tablet
function isMobileOrTablet() {
  // Check for touch support and screen size
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isSmallScreen = window.innerWidth <= 1024
  
  // Check user agent for mobile/tablet patterns
  const userAgent = navigator.userAgent.toLowerCase()
  const mobileKeywords = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i
  const isMobileUA = mobileKeywords.test(userAgent)
  
  return (isTouchDevice && isSmallScreen) || isMobileUA
}

// Component to conditionally render post-processing based on animation state
function PostProcessingLayer({ postProcessing, hdriBlend = 0 }) {
  // Only render effects when master switch is enabled
  if (!postProcessing.effectComposerEnabled) {
    return null // Disable all effects
  }
  
  return (
    <EffectComposer>
      {postProcessing.toneMappingEnabled && (
        <ToneMapping
          mode={postProcessing.toneMappingMode}
        />
      )}
      <Bloom
        intensity={0.38}
        luminanceThreshold={1.0}
        luminanceSmoothing={0.9}
        blendFunction={BlendFunction.ADD}
      />
      <HueSaturation
        hue={0}
        saturation={0}
      />
      <Sepia
        intensity={0.1}
      />
      {postProcessing.noiseEnabled && (
        <Noise
          opacity={postProcessing.noiseOpacity}
          premultiply={postProcessing.usePremultiply}
          blendFunction={postProcessing.noiseBlendMode}
        />
      )}
      <Vignette
        offset={0.10}
        darkness={1}
        eskil={false} 
        blendFunction={BlendFunction.NORMAL}
      />
    </EffectComposer>
  )
}

// Component to read and display the actual renderer DPR
function DPRDisplay({ show, hdriBlend = 0, scene3Blend = 0 }) {
  const [renderDpr, setRenderDpr] = useState(0)
  const [deviceDpr, setDeviceDpr] = useState(window.devicePixelRatio)
  const [screenResolution, setScreenResolution] = useState(`${window.screen.width}x${window.screen.height}`)
  
  // Determine current scene
  const getCurrentScene = () => {
    if (hdriBlend < 0.5) return 'Scene 1'
    if (scene3Blend < 0.5) return 'Scene 2'
    return 'Scene 3'
  }
  
  useEffect(() => {
    if (!show) return
    
    // Get the canvas element
    const canvas = document.querySelector('canvas')
    if (!canvas) return
    
    // Calculate actual rendering DPR from canvas dimensions
    const updateRenderDpr = () => {
      const rect = canvas.getBoundingClientRect()
      const actualDpr = canvas.width / rect.width
      setRenderDpr(actualDpr)
    }
    
    // Update device DPR (in case window moves between displays)
    const updateDeviceDpr = () => {
      setDeviceDpr(window.devicePixelRatio)
    }
    
    // Update screen resolution
    const updateScreenResolution = () => {
      setScreenResolution(`${window.screen.width}x${window.screen.height}`)
    }
    
    updateRenderDpr()
    updateDeviceDpr()
    updateScreenResolution()
    
    // Update on resize
    window.addEventListener('resize', updateRenderDpr)
    window.addEventListener('resize', updateDeviceDpr)
    window.addEventListener('resize', updateScreenResolution)
    const observer = new ResizeObserver(updateRenderDpr)
    observer.observe(canvas)
    
    return () => {
      window.removeEventListener('resize', updateRenderDpr)
      window.removeEventListener('resize', updateDeviceDpr)
      window.removeEventListener('resize', updateScreenResolution)
      observer.disconnect()
    }
  }, [show])
  
  if (!show) return null
  
  return (
    <div style={{
      position: 'fixed',
      top: '120px',
      left: '10px',
      padding: '8px 12px',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: '#00ff00',
      fontFamily: 'monospace',
      fontSize: '14px',
      borderRadius: '4px',
      zIndex: 9999,
      pointerEvents: 'none',
      lineHeight: '1.5'
    }}>
      <div>Render DPR: {renderDpr.toFixed(2)}</div>
      <div>Device DPR: {deviceDpr.toFixed(2)}</div>
      <div>Resolution: {screenResolution}</div>
      <div>Current Scene: {getCurrentScene()}</div>
      <div>hdriBlend: {hdriBlend.toFixed(2)}</div>
      <div>scene3Blend: {scene3Blend.toFixed(2)}</div>
    </div>
  )
}

// Component to apply camera roll (dutch angle) around Z-axis
// Component to update camera zoom dynamically
function CameraZoomUpdater({ zoom }) {
  const { camera } = useThree()
  
  useEffect(() => {
    if (camera) {
      camera.zoom = zoom
      camera.updateProjectionMatrix()
    }
  }, [zoom, camera])
  
  return null
}

// Component that manages auto-rotation based on scene and orientation
function AppContent({ cameraRotation, children }) {
  const { hdriBlend, scene3Blend } = useAnimation()
  const [autoRotation, setAutoRotation] = useState(0)
  const [targetRotation, setTargetRotation] = useState(0)
  const animationStartTimeRef = useRef(null)
  const startRotationRef = useRef(0)
  const isOrientationChangeRef = useRef(false) // Track if change is from orientation vs scene transition
  const shouldAnimateRef = useRef(false) // Track if we should animate the rotation
  const isReversingRef = useRef(false) // Track if we're returning to Scene 1
  const animationDurationMs = 3000

  // Expose hdriBlend to window for camera controls to access
  useEffect(() => {
    window.__hdriBlend = hdriBlend
  }, [hdriBlend])

  // Check rotation correctness when hdriBlend transition completes
  useEffect(() => {
    // Only check when fully in Scene 1 or Scene 2 (not mid-transition)
    if (hdriBlend !== 0 && hdriBlend !== 1) return

    // Wait for any animations to complete before validating
    const timeoutId = setTimeout(() => {
      // Double-check animation isn't running
      if (shouldAnimateRef.current) return

      const isLandscape = window.innerWidth > window.innerHeight
      let correctRotation

      if (hdriBlend === 0) {
        // Scene 1: Should always be upright
        correctRotation = 0
      } else if (hdriBlend === 1) {
        // Scene 2: Should match device orientation
        correctRotation = isLandscape ? Math.PI / 2 : 0
      }

      // Check current rotation from ref
      const currentRotation = startRotationRef.current
      
      // Only correct if there's a significant difference
      if (Math.abs(currentRotation - correctRotation) > 0.01) {
        setAutoRotation(correctRotation)
        setTargetRotation(correctRotation)
        startRotationRef.current = correctRotation
        animationStartTimeRef.current = null
        shouldAnimateRef.current = false
      }
    }, 200) // Longer delay to ensure animation completes

    return () => clearTimeout(timeoutId)
  }, [hdriBlend]) // Only depend on hdriBlend, not autoRotation

  // Listen for button press to start camera rotation immediately (before fade)
  useEffect(() => {
    const handleButtonPress = (event) => {
      const isReversed = event.detail?.isReversed || false

      // Track if we're reversing
      if (isReversed) {
        isReversingRef.current = true
      } else {
        isReversingRef.current = false
      }

      // Only handle reverse transitions (Scene 2 -> Scene 1)
      if (isReversed) {
        setTargetRotation(0)
        
        // Only animate if we're not already at 0
        if (autoRotation !== 0) {
          startRotationRef.current = autoRotation
          animationStartTimeRef.current = performance.now()
          shouldAnimateRef.current = true
        } else {
          // Already at 0, just ensure state is correct
          setAutoRotation(0)
          startRotationRef.current = 0
          animationStartTimeRef.current = null
          shouldAnimateRef.current = false
        }
      }
    }

    window.addEventListener('animation-start', handleButtonPress)
    return () => {
      window.removeEventListener('animation-start', handleButtonPress)
    }
  }, [autoRotation])

  // Single unified orientation handler
  useEffect(() => {
    const handleOrientation = () => {
      const isLandscape = window.innerWidth > window.innerHeight
      
      // Determine what rotation should be based on current scene
      let desiredRotation
      
      if (hdriBlend === 0) {
        // Scene 1: Always upright
        desiredRotation = 0
      } else if (hdriBlend > 0) {
        // Scene 2, Scene 3, or transitioning: Respect device orientation
        desiredRotation = isLandscape ? Math.PI / 2 : 0
      }
      
      // If rotation needs to change
      if (desiredRotation !== targetRotation) {
        setTargetRotation(desiredRotation)
        
        // Mark this as an orientation change (not initial scene transition)
        isOrientationChangeRef.current = true
        
        // Snap immediately for orientation changes - NO animation
        setAutoRotation(desiredRotation)
        startRotationRef.current = desiredRotation
        animationStartTimeRef.current = null
        shouldAnimateRef.current = false
      }
    }

    // Don't call on mount - let the hdriBlend effect handle initial transitions
    // Only listen for orientation changes
    window.addEventListener('orientationchange', handleOrientation)
    window.addEventListener('resize', handleOrientation)

    return () => {
      window.removeEventListener('orientationchange', handleOrientation)
      window.removeEventListener('resize', handleOrientation)
    }
  }, [hdriBlend, targetRotation, scene3Blend])

  // Handle scene transitions (animate from Scene 1 to Scene 2)
  useEffect(() => {
    // Skip if this change was triggered by orientation change
    if (isOrientationChangeRef.current) {
      isOrientationChangeRef.current = false
      return
    }

    // Don't trigger when returning to Scene 1 (reverse transition)
    if (hdriBlend === 0 || isReversingRef.current) {
      // Clear reversing flag when we reach Scene 1
      if (hdriBlend === 0) {
        isReversingRef.current = false
      }
      return
    }

    const isLandscape = window.innerWidth > window.innerHeight
    let desiredRotation

    if (hdriBlend > 0) {
      // Scene 2 or transitioning: Respect device orientation
      desiredRotation = isLandscape ? Math.PI / 2 : 0
    }

    if (desiredRotation !== targetRotation) {
      setTargetRotation(desiredRotation)
      
      // Animate the transition
      startRotationRef.current = autoRotation
      animationStartTimeRef.current = performance.now()
      shouldAnimateRef.current = true
    }
  }, [hdriBlend])

  // Animation loop - smoothly interpolate rotation for scene transitions
  useEffect(() => {
    // Only animate if explicitly told to
    if (!shouldAnimateRef.current || animationStartTimeRef.current === null) return

    let animationFrameId = null
    const animate = (timestamp) => {
      const elapsed = timestamp - animationStartTimeRef.current
      const progress = Math.min(elapsed / animationDurationMs, 1)

      // Apply easeInOutCubic easing
      const easeInOutCubic = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2

      // Interpolate from start rotation to target rotation
      const interpolated = startRotationRef.current + (targetRotation - startRotationRef.current) * easeInOutCubic

      setAutoRotation(interpolated)

      // Continue animation until complete
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate)
      } else {
        // Animation complete - set exact target value
        setAutoRotation(targetRotation)
        animationStartTimeRef.current = null
        shouldAnimateRef.current = false
      }
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [targetRotation, animationDurationMs])

  // Update rotation passed to TripleSceneContents: combine manual slider + auto-rotation
  const effectiveRotation = cameraRotation.rotationZ + autoRotation

  // Memoize the children render to avoid unnecessary re-renders when rotation hasn't changed
  return useMemo(() => children(effectiveRotation, hdriBlend, scene3Blend), [children, effectiveRotation, hdriBlend, scene3Blend])
}


export default function App() {
  const [showPerf, setShowPerf] = useState(false)
  const [showHelpers, setShowHelpers] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const [scenesData, setScenesData] = useState(null)
  const [isAtPOI, setIsAtPOI] = useState(false)
  const [activePOI, setActivePOI] = useState(null)
  const [cameraZoom, setCameraZoom] = useState(2000)
  const [viewportInfo, setViewportInfo] = useState({ width: window.innerWidth, height: window.innerHeight })
  
  // Compute portrait mode
  const isPortrait = window.innerHeight > window.innerWidth
  
  // Add tablet class to body for CSS targeting
  useEffect(() => {
    const updateDeviceClass = () => {
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
      const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
      const isLandscape = window.innerWidth > window.innerHeight
      const isTabletLandscape = isTablet && isLandscape
      
      if (isTabletLandscape) {
        document.body.classList.add('tablet-landscape')
      } else {
        document.body.classList.remove('tablet-landscape')
      }
    }
    
    updateDeviceClass()
    window.addEventListener('resize', updateDeviceClass)
    window.addEventListener('orientationchange', updateDeviceClass)
    
    return () => {
      window.removeEventListener('resize', updateDeviceClass)
      window.removeEventListener('orientationchange', updateDeviceClass)
    }
  }, [])
  
  // Handle POI state changes
  const handlePOIStateChange = (atPOI, poiName = null) => {
    setIsAtPOI(atPOI)
    setActivePOI(atPOI ? poiName : null)
  }
  
  // Store the handleBackToDefault function from HotspotsOverlay
  const backToDefaultHandlerRef = useRef(null)
  
  const handleBackToDefaultReady = useCallback((handler) => {
    backToDefaultHandlerRef.current = handler
  }, [])
  
  const handleBackToDefault = useCallback(() => {
    if (backToDefaultHandlerRef.current) {
      backToDefaultHandlerRef.current()
    }
  }, [])
  
  // Update camera zoom based on device orientation and size
  useEffect(() => {
    const updateCameraZoom = () => {
      const isPortrait = window.innerHeight > window.innerWidth
      const isMobile = window.innerWidth <= 768
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024
      const newZoom = isPortrait ? 900 : 2000
      
      // Update viewport info for debug display
      setViewportInfo({
        width: window.innerWidth,
        height: window.innerHeight,
        isPortrait,
        isMobile,
        isTablet,
        newZoom
      })
      
      setCameraZoom(newZoom)
    }
    
    updateCameraZoom()
    
    // Listen for orientation changes and window resize
    window.addEventListener('orientationchange', updateCameraZoom)
    window.addEventListener('resize', updateCameraZoom)
    
    return () => {
      window.removeEventListener('orientationchange', updateCameraZoom)
      window.removeEventListener('resize', updateCameraZoom)
    }
  }, [])
  
  // Camera rotation (no controls - hardcoded to 0)
  const cameraRotation = { rotationZ: 0 }

  // Hardcoded post-processing settings
  const postProcessing = {
    effectComposerEnabled: true,
    toneMappingEnabled: true,
    toneMappingMode: ToneMappingMode.CINEON,
    noiseEnabled: true,
    noiseOpacity: 0.35,
    noiseBlendMode: BlendFunction.NORMAL,
    usePremultiply: true,
  }
  
  // Determine DPR based on device type
  const dprSetting = useMemo(() => {
    const isMobile = isMobileOrTablet()
    // Desktop: always 1, Mobile/Tablet: use device DPR clamped to max 1.5 for performance
    return isMobile ? [1, 1.5] : 1
  }, [])

  useEffect(() => {
    // Check for ?dev=true URL parameter on mount
    const urlParams = new URLSearchParams(window.location.search)
    const isDevMode = urlParams.get('dev') === 'true'
    
    if (isDevMode) {
      setShowPerf(true)
      setShowHelpers(true)
      setShowControls(true)
    }

    const handleKeyPress = (event) => {
      if (event.key === 'p' || event.key === 'P') {
        setShowPerf(prev => !prev)
        setShowHelpers(prev => !prev)
        setShowControls(prev => !prev)
      }
    }
    
    // Prevent pinch-zoom and double-tap zoom on mobile
    const handleWheel = (event) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
      }
    }
    
    const handleTouchMove = (event) => {
      // Prevent pinch-zoom gesture (2+ touch points)
      if (event.touches.length > 1) {
        event.preventDefault()
      }
    }
    
    const handleGestureStart = (event) => {
      event.preventDefault()
    }

    window.addEventListener('keydown', handleKeyPress)
    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('gesturestart', handleGestureStart, { passive: false })
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('gesturestart', handleGestureStart)
    }
  }, [])

  return (
    <AnimationProvider>
      <AppContent cameraRotation={cameraRotation}>
        {(effectiveRotation, hdriBlend, scene3Blend) => {
          return (
            <>
              <Leva hidden={!showControls} />
              <Canvas 
              shadows 
              orthographic 
              dpr={dprSetting}
              frameloop="always"
              camera={{ 
                position: [0, 10, 15], 
                zoom: cameraZoom,
                near: 0.1,
                far: 1000
              }}
              gl={{
                toneMapping: THREE.NoToneMapping, // Disable - we apply ACES in shader
                outputColorSpace: THREE.SRGBColorSpace // Final output is sRGB for display
              }}
            >
              <color attach="background" args={['#000000']} />
              {showPerf && <Perf position="top-left" />}
              <CameraZoomUpdater zoom={cameraZoom} />
              <TripleSceneContents 
                showHelpers={showHelpers}
                selectiveBloomEnabled={postProcessing.selectiveBloomEnabled}
                onScenesReady={setScenesData}
                isAtPOI={isAtPOI}
                activePOI={activePOI}
                cameraZoom={cameraZoom}
                cameraRotation={effectiveRotation}
              />
              <Hotspots scenesRef={scenesData} cameraRef={scenesData?.camera} />
              <PostProcessingLayer postProcessing={postProcessing} />
            </Canvas>
            <DPRDisplay show={showPerf} hdriBlend={hdriBlend} scene3Blend={scene3Blend} />
            <Title activePOI={activePOI} />
            <TextBox isAtPOI={isAtPOI} activePOI={activePOI} hdriBlend={hdriBlend} onBackToDefault={handleBackToDefault} />
            <StandaloneBackButton isAtPOI={isAtPOI} hdriBlend={hdriBlend} onBackToDefault={handleBackToDefault} />
            <Scene2BackButtonTablet isAtPOI={isAtPOI} activePOI={activePOI} hdriBlend={hdriBlend} onBackToDefault={handleBackToDefault} />
            <Scene2BackButtonDesktop isAtPOI={isAtPOI} activePOI={activePOI} hdriBlend={hdriBlend} onBackToDefault={handleBackToDefault} />
            <HotspotsOverlay isAtPOI={isAtPOI} onPOIStateChange={handlePOIStateChange} scenesRef={scenesData} cameraZoom={cameraZoom} cameraRotation={effectiveRotation} onBackToDefaultReady={handleBackToDefaultReady} />
            <MoveButton isAtPOI={isAtPOI} isPortrait={isPortrait} />
            <LogoGroup isAtPOI={isAtPOI} hdriBlend={hdriBlend} />
            <WoomeraBadge isAtPOI={isAtPOI} hdriBlend={hdriBlend} />
            <StageSpecs activePOI={activePOI} isAtPOI={isAtPOI} />
          </>
        )}}
      </AppContent>
    </AnimationProvider>
  )
}